import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';
import { ForgotPasswordForm } from '../components/forms/ForgotPasswordForm';
import { PasswordResetForm } from '../components/forms/PasswordResetForm';


/**
 * Password Reset Page
 * Handles both flows:
 * 1. Request password reset (no token) - shows ForgotPasswordForm
 * 2. Confirm password reset (with token) - shows PasswordResetForm
 *
 * Supports two Supabase auth flows:
 * - PKCE flow: /reset-password?code=xxx (current default)
 * - Implicit flow: /reset-password#access_token=xxx&type=recovery (legacy fallback)
 */
export const PasswordResetPage: React.FC = () => {
  const navigate = useNavigate();
  const [resetToken, setResetToken] = useState<string | null>(null);
  const [isExchangingCode, setIsExchangingCode] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);

  // Check for reset token in URL (PKCE code or hash fragment)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');

    if (code) {
      // PKCE flow: exchange the code for a session
      setIsExchangingCode(true);
      supabase.auth
        .exchangeCodeForSession(code)
        .then(({ error }) => {
          if (error) {
            console.error('Code exchange error:', error);
            setCodeError(
              'This reset link has expired or is invalid. Please request a new one.'
            );
          } else {
            setResetToken(code);
          }
        })
        .catch((err) => {
          console.error('Unexpected code exchange error:', err);
          setCodeError(
            'Something went wrong. Please request a new reset link.'
          );
        })
        .finally(() => {
          setIsExchangingCode(false);
        });
      return;
    }

    // Legacy implicit flow fallback
    // Format: /reset-password#access_token=xxx&type=recovery
    const hash = window.location.hash;

    if (hash) {
      const hashParams = new URLSearchParams(hash.substring(1));
      const token = hashParams.get('access_token');
      const type = hashParams.get('type');

      if (token && type === 'recovery') {
        setResetToken(token);
      }
    }
  }, []);

  const handleRequestSuccess = () => {
    // Success message is already shown by the form
    // User should check their email
  };

  const handleResetSuccess = () => {
    // After successful password reset, redirect to home page
    // User is automatically signed in by Supabase
    setTimeout(() => {
      navigate('/popular', { replace: true });
    }, 2000); // Give user time to see success message
  };

  const handleError = (error: string) => {
    console.error('Password reset error:', error);
    // Error is already displayed by the form component
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container max-w-2xl mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            {isExchangingCode
              ? 'Verifying...'
              : resetToken
                ? 'Reset Your Password'
                : 'Forgot Your Password?'}
          </h1>
          <p className="text-muted-foreground">
            {isExchangingCode
              ? 'Please wait while we verify your reset link.'
              : resetToken
                ? "You're almost there! Enter your new password below."
                : "No worries! Enter your email and we'll send you a reset link."}
          </p>
        </div>

        {/* Form Content */}
        <div className="max-w-md mx-auto">
          {isExchangingCode ? (
            <div className="flex flex-col items-center py-8">
              <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mb-4" />
              <p className="text-muted-foreground">Verifying reset link...</p>
            </div>
          ) : codeError ? (
            <div className="space-y-4">
              <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
                <p className="text-sm text-destructive">{codeError}</p>
              </div>
              <ForgotPasswordForm
                onSuccess={handleRequestSuccess}
                onError={handleError}
              />
            </div>
          ) : resetToken ? (
            // Show password reset form when token is present
            <PasswordResetForm
              onSuccess={handleResetSuccess}
              onError={handleError}
            />
          ) : (
            // Show forgot password form when no token
            <ForgotPasswordForm
              onSuccess={handleRequestSuccess}
              onError={handleError}
            />
          )}
        </div>

        {/* Additional Help */}
        {!resetToken && (
          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              Remember your password?{' '}
              <button
                onClick={() => {
                  navigate('/popular');
                  // Open the user modal after navigating
                  setTimeout(() => {
                    window.dispatchEvent(new CustomEvent('open-user-modal'));
                  }, 100);
                }}
                className="text-primary hover:underline font-medium"
              >
                Sign in
              </button>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PasswordResetPage;
